import React, { useState, useEffect } from "react";
import { Modal, Table, Spinner, Alert, Badge } from "react-bootstrap";
import { IconPlus, IconMinus, IconEye } from "@tabler/icons-react";
import { getRequest } from "services/api";
import PrintJason from "utils/printJson";

const ItemHistoryModal = ({ show, onHide, itemId, itemName }) => {
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;

  useEffect(() => {
    if (show && itemId) {
      fetchHistory(1);
    } else if (!show) {
      // Reset pagination when modal closes
      setCurrentPage(1);
      setTotalPages(1);
      setTotalItems(0);
    }
  }, [show, itemId]);

  const fetchHistory = async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const response = await getRequest(
        `/inventory/${itemId}/history?page=${page}&limit=${itemsPerPage}&include=warehouse`,
      );
      setHistoryData(response.data || []);

      // Set pagination data
      if (response.pagination || response.meta) {
        const pagination = response.pagination || response.meta;
        setTotalPages(pagination.last_page || 1);
        setTotalItems(pagination.total || 0);
      }

      setCurrentPage(page);
    } catch (err) {
      setError(err.message || "Failed to fetch history");
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (type) => {
    switch (type?.toLowerCase()) {
      case "in":
        return <IconPlus size={16} color="#059669" />;
      case "out":
        return <IconMinus size={16} color="#dc2626" />;
      default:
        return <IconEye size={16} color="#6b7280" />;
    }
  };

  const getActionBadge = (type) => {
    switch (type?.toLowerCase()) {
      case "in":
        return (
          <Badge bg="success" text="white">
            Masuk
          </Badge>
        );
      case "out":
        return (
          <Badge bg="danger" text="white">
            Keluar
          </Badge>
        );
      default:
        return (
          <Badge bg="secondary" text="white">
            Lainnya
          </Badge>
        );
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered className="workshop-modal-scope">
      <Modal.Header closeButton>
        <Modal.Title>History - {itemName}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <Spinner animation="border" />
            <div style={{ marginTop: "8px" }}>Loading history...</div>
          </div>
        ) : error ? (
          <Alert variant="danger">
            <strong>Error:</strong> {error}
          </Alert>
        ) : historyData.length > 0 ? (
          <>
            <div className="mb-3 text-muted">
              Showing {historyData.length} of {totalItems} entries (Page{" "}
              {currentPage} of {totalPages})
            </div>
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Tanggal</th>
                  <th>Aksi</th>
                  <th>Jumlah</th>
                  <th>Stok Sebelum</th>
                  <th>Stok Sesudah</th>
                  <th>User</th>
                  <th>Catatan</th>
                </tr>
              </thead>
              <tbody>
                {historyData.map((entry, index) => (
                  <tr key={entry.id || index}>
                    <td>
                      {`${new Date(
                        entry.date_time || entry.created_at,
                      ).toLocaleDateString("id-ID")}, ${new Date(
                        entry.date_time || entry.created_at,
                      ).toLocaleTimeString("id-ID", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}`}
                    </td>
                    <td>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        {entry.warehouse_name && (
                          <Badge bg="info" text="white">
                            {entry.warehouse_name}
                          </Badge>
                        )}
                        {getActionIcon(entry.type)}
                        {getActionBadge(entry.type)}
                      </div>
                    </td>
                    <td>{entry.quantity}</td>
                    <td>{entry.previous_stock}</td>
                    <td>{entry.new_stock}</td>
                    <td>{entry.user}</td>
                    <td>
                      [{entry.stock_transfer?.notes || "-"}] <br />{" "}
                      {entry.reason || "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-center mt-3">
                <nav>
                  <ul className="pagination">
                    <li
                      className={`page-item ${
                        currentPage === 1 ? "disabled" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => fetchHistory(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>
                    </li>

                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }

                      return (
                        <li
                          key={pageNum}
                          className={`page-item ${
                            currentPage === pageNum ? "active" : ""
                          }`}
                        >
                          <button
                            className="page-link"
                            onClick={() => fetchHistory(pageNum)}
                          >
                            {pageNum}
                          </button>
                        </li>
                      );
                    })}

                    <li
                      className={`page-item ${
                        currentPage === totalPages ? "disabled" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => fetchHistory(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
            <PrintJason data={historyData} />
          </>
        ) : (
          <div style={{ textAlign: "center", padding: "40px" }}>
            No history data found for this item.
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ItemHistoryModal;
